// frontend/src/pages/Orders.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { getUserOrders } from "../api";
import { useAuth } from "../context/AuthContext";
import CurrencyDisplay from "../components/UI/CurrencyDisplay";
import Loader from "../components/UI/Loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import {
  faBox,
  faTruck,
  faClock,
  faCheckCircle,
  faTimes,
  faEye,
  faSearch,
  faFilter,
  faSeedling,
  faPhone,
  faEnvelope,
  faCalendarAlt,
  faUser,
  faGlobe,
  faShip,
  faBuilding,
  faArrowRight,
  faSpinner,
  faExclamationTriangle,
  faCheckDouble,
  faShippingFast,
  faBoxOpen,
  faWarehouse,
} from "@fortawesome/free-solid-svg-icons";

// ============================================
// BRAND CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

// ============================================
// SUPPLIER CONSTANTS
// ============================================
const SUPPLIER = {
  name: "Mylasha",
  country: "Malaysia",
  city: "Kuala Lumpur",
  portLoading: "Port Klang, Malaysia",
  portDischarge: "Freetown, Sierra Leone",
};

// ============================================
// HELPERS
// ============================================
const getStatusColor = (status) => {
  const colors = {
    pending: "#D9A321",
    processing: "#3b82f6",
    confirmed: "#8b5cf6",
    shipped: "#8b5cf6",
    out_for_delivery: "#f59e0b",
    delivered: "#16a34a",
    cancelled: "#dc2626",
    refunded: "#6b7280",
  };
  return colors[status] || "#5A5A5A";
};

const getStatusIcon = (status) => {
  const icons = {
    pending: faClock,
    processing: faSpinner,
    confirmed: faCheckCircle,
    shipped: faShippingFast,
    out_for_delivery: faTruck,
    delivered: faCheckDouble,
    cancelled: faTimes,
    refunded: faTimes,
  };
  return icons[status] || faClock;
};

const getStatusLabel = (status) => {
  const labels = {
    pending: "⏳ Pending",
    processing: "⚙️ Processing",
    confirmed: "✅ Confirmed",
    shipped: "🚢 Shipped",
    out_for_delivery: "📬 Out for Delivery",
    delivered: "📦 Delivered",
    cancelled: "❌ Cancelled",
    refunded: "↩️ Refunded",
  };
  return labels[status] || status;
};

const STATUS_OPTIONS = [
  { value: "all", label: "All Orders" },
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "confirmed", label: "Confirmed" },
  { value: "shipped", label: "Shipped" },
  { value: "out_for_delivery", label: "Out for Delivery" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
];

// ============================================
// MAIN COMPONENT
// ============================================
const Orders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);

  // ============================================
  // FETCH ORDERS
  // ============================================
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const { data } = await getUserOrders();
        setOrders(data);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  // ============================================
  // COMPUTED
  // ============================================
  if (loading) return <Loader />;

  const isFarmer = user?.userType === "farmer";
  const bulkOrders = orders.filter(
    (o) => o.orderType === "bulk_farmer" || o.totalQuantity >= 100,
  );

  const filteredOrders = orders
    .filter((o) => filter === "all" || o.status === filter)
    .filter((o) => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        o.orderNumber?.toLowerCase().includes(search) ||
        o.userName?.toLowerCase().includes(search) ||
        o.customerName?.toLowerCase().includes(search) ||
        o.items?.some((item) => item.name?.toLowerCase().includes(search))
      );
    });

  // ============================================
  // RENDER
  // ============================================
  return (
    <motion.div
      className="orders-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* ========== HERO ========== */}
      <section className="orders-hero">
        <div className="orders-hero__overlay" />
        <div className="container orders-hero__content">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <FontAwesomeIcon icon={faBox} className="orders-hero__icon" />
            <h1>My Orders</h1>
            <p>
              Track and manage all your orders from Kenali Enterprise. All
              products are sourced from {SUPPLIER.name} in {SUPPLIER.country}.
            </p>
            <div className="orders-hero__badges">
              <span>
                <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country} Origin
              </span>
              <span>
                <FontAwesomeIcon icon={faShip} /> {SUPPLIER.portLoading}
              </span>
            </div>
            <a
              href="https://wa.me/23231030808"
              target="_blank"
              rel="noopener noreferrer"
              className="orders-hero__whatsapp"
            >
              <FontAwesomeIcon icon={faWhatsapp} />
              Need help? WhatsApp us
            </a>
          </motion.div>
        </div>
      </section>

      {/* ========== MAIN CONTENT ========== */}
      <div className="container orders-container">
        {/* Farmer Notice */}
        {isFarmer && bulkOrders.length > 0 && (
          <motion.div
            className="farmer-notice"
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
          >
            <FontAwesomeIcon icon={faBox} />
            <div>
              <strong>🌾 Farmer Account</strong>
              <span>
                Bulk orders receive priority shipping and dedicated support.
              </span>
            </div>
            <span className="farmer-notice__count">
              {bulkOrders.length} Bulk Order{bulkOrders.length !== 1 ? "s" : ""}
            </span>
          </motion.div>
        )}

        {/* Empty State */}
        {orders.length === 0 ? (
          <motion.div
            className="orders-empty"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            <div className="orders-empty__icon">📦</div>
            <h3>No Orders Yet</h3>
            <p>You haven't placed any orders yet.</p>
            <div className="orders-empty__actions">
              <Link to="/products" className="shop-link">
                <FontAwesomeIcon icon={faSeedling} />
                Start Shopping
              </Link>
              <a
                href="https://wa.me/23231030808"
                target="_blank"
                rel="noopener noreferrer"
                className="whatsapp-link"
              >
                <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp Us
              </a>
            </div>
          </motion.div>
        ) : (
          <>
            {/* Filters */}
            <div className="orders-filters">
              <div className="filters-scroll">
                <FontAwesomeIcon icon={faFilter} className="filter-icon" />
                {STATUS_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setFilter(option.value)}
                    className={`filter-btn ${filter === option.value ? "active" : ""}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <div className="search-wrapper">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>

            {/* Results Count */}
            <div className="orders-count">
              <span>
                Showing {filteredOrders.length} order
                {filteredOrders.length !== 1 ? "s" : ""}
              </span>
              {filter !== "all" && (
                <button
                  onClick={() => setFilter("all")}
                  className="clear-filter"
                >
                  <FontAwesomeIcon icon={faTimes} /> Clear Filter
                </button>
              )}
            </div>

            {/* Orders List */}
            <div className="orders-list">
              {filteredOrders.length === 0 ? (
                <div className="orders-no-results">
                  No orders found matching your criteria.
                </div>
              ) : (
                <AnimatePresence>
                  {filteredOrders.map((order, index) => {
                    const isBulk =
                      order.orderType === "bulk_farmer" ||
                      order.totalQuantity >= 100;
                    return (
                      <motion.div
                        key={order._id}
                        className={`order-card ${isBulk ? "bulk" : ""}`}
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{ y: -2 }}
                      >
                        <Link
                          to={`/orders/${order._id}`}
                          className="order-card__link"
                        >
                          <div className="order-card__left">
                            <div
                              className={`order-icon ${isBulk ? "bulk" : "regular"}`}
                            >
                              <FontAwesomeIcon
                                icon={isBulk ? faTruck : faBox}
                              />
                            </div>
                            <div>
                              <div className="order-number">
                                Order #{order.orderNumber}
                                {isBulk && (
                                  <span className="bulk-badge">BULK</span>
                                )}
                                {order.isPreOrder && (
                                  <span className="preorder-badge">
                                    <FontAwesomeIcon icon={faClock} /> Pre-Order
                                  </span>
                                )}
                              </div>
                              <div className="order-meta">
                                <span>
                                  <FontAwesomeIcon icon={faCalendarAlt} />
                                  {new Date(
                                    order.createdAt,
                                  ).toLocaleDateString()}
                                </span>
                                <span>
                                  <FontAwesomeIcon icon={faBox} />
                                  {order.items?.length || 0} items
                                </span>
                                <span>
                                  <FontAwesomeIcon icon={faWarehouse} />
                                  {order.totalQuantity || 0}kg
                                </span>
                                <span>
                                  <FontAwesomeIcon icon={faGlobe} />
                                  {SUPPLIER.country}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="order-card__right">
                            <div className="order-total">
                              <div className="total-label">Total</div>
                              <div className="total-amount">
                                <CurrencyDisplay
                                  amountUSD={
                                    order.total?.usd ||
                                    order.totalAmount?.usd ||
                                    0
                                  }
                                  compact
                                />
                              </div>
                            </div>

                            <div
                              className="order-status"
                              style={{
                                background: getStatusColor(order.status),
                              }}
                            >
                              <FontAwesomeIcon
                                icon={getStatusIcon(order.status)}
                              />
                              {getStatusLabel(order.status)}
                            </div>

                            <div className="order-view">
                              View Details <FontAwesomeIcon icon={faEye} />
                            </div>
                          </div>
                        </Link>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              )}
            </div>

            {/* Support Section */}
            <div className="orders-support">
              <FontAwesomeIcon icon={faWhatsapp} />
              <div>
                <strong>Need help with your order?</strong>
                <span>
                  Contact our support team for assistance with tracking,
                  returns, or any questions.
                </span>
              </div>
              <a
                href="https://wa.me/23231030808"
                target="_blank"
                rel="noopener noreferrer"
                className="support-link"
              >
                <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp Support
              </a>
            </div>
          </>
        )}
      </div>

      <style>{`
        /* ========== HERO ========== */
        .orders-hero {
          background: linear-gradient(135deg, #006B2E 0%, #014421 100%);
          color: #FFFFFF;
          padding: 2.5rem 1.5rem;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        @media (min-width: 768px) {
          .orders-hero {
            padding: 3rem 2rem;
          }
        }
        .orders-hero__overlay {
          position: absolute;
          top: -50%;
          right: -20%;
          width: 300px;
          height: 300px;
          background: rgba(217, 163, 33, 0.05);
          border-radius: 50%;
        }
        @media (min-width: 768px) {
          .orders-hero__overlay {
            width: 500px;
            height: 500px;
          }
        }
        .orders-hero__content {
          position: relative;
          z-index: 2;
        }
        .orders-hero__icon {
          font-size: 2rem;
          color: #D9A321;
          margin-bottom: 0.75rem;
        }
        @media (min-width: 768px) {
          .orders-hero__icon {
            font-size: 2.5rem;
            margin-bottom: 1rem;
          }
        }
        .orders-hero h1 {
          font-size: clamp(1.75rem, 4vw, 2.5rem);
          margin-bottom: 0.5rem;
          font-family: "Montserrat", sans-serif;
        }
        .orders-hero p {
          font-size: 0.938rem;
          max-width: 600px;
          margin: 0 auto;
          opacity: 0.9;
        }
        @media (min-width: 768px) {
          .orders-hero p {
            font-size: 1rem;
          }
        }
        .orders-hero__badges {
          display: flex;
          justify-content: center;
          gap: 1rem;
          flex-wrap: wrap;
          margin-top: 0.5rem;
          font-size: 0.7rem;
          opacity: 0.8;
        }
        .orders-hero__badges span {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
        }
        .orders-hero__whatsapp {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: #25d366;
          background: #ffffff;
          padding: 0.4rem 1rem;
          border-radius: 20px;
          font-size: 0.813rem;
          font-weight: 500;
          text-decoration: none;
          margin-top: 0.75rem;
          transition: all 0.3s ease;
        }
        .orders-hero__whatsapp:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        /* ========== CONTAINER ========== */
        .orders-container {
          padding: 2rem 1.5rem;
          max-width: 1000px;
          margin: 0 auto;
        }
        @media (max-width: 480px) {
          .orders-container {
            padding: 1.5rem 0.75rem;
          }
        }

        /* ========== FARMER NOTICE ========== */
        .farmer-notice {
          background: #e8f5e9;
          padding: 0.75rem 1.25rem;
          border-radius: 12px;
          margin-bottom: 2rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.75rem;
          border: 1px solid #c8e6c9;
        }
        .farmer-notice svg {
          color: #006B2E;
          font-size: 1.25rem;
        }
        .farmer-notice div {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
          color: #5A5A5A;
          font-size: 0.875rem;
        }
        .farmer-notice div strong {
          color: #006B2E;
        }
        .farmer-notice__count {
          font-size: 0.75rem;
          color: #006B2E;
          font-weight: 600;
          background: #ffffff;
          padding: 0.15rem 0.5rem;
          border-radius: 12px;
        }

        /* ========== EMPTY STATE ========== */
        .orders-empty {
          text-align: center;
          padding: 3rem 1rem;
        }
        .orders-empty__icon {
          font-size: 4rem;
          margin-bottom: 1rem;
        }
        .orders-empty h3 {
          color: #006B2E;
          font-family: "Montserrat", sans-serif;
        }
        .orders-empty p {
          color: #5A5A5A;
          margin: 0.5rem 0 1.5rem;
        }
        .orders-empty__actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }
        .shop-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 2rem;
          background: #D9A321;
          color: #FFFFFF;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s ease;
        }
        .shop-link:hover {
          background: #B8860B;
          transform: translateY(-2px);
        }
        .whatsapp-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 2rem;
          background: #25d366;
          color: #FFFFFF;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s ease;
        }
        .whatsapp-link:hover {
          background: #1da851;
          transform: translateY(-2px);
        }

        /* ========== FILTERS ========== */
        .orders-filters {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 1.5rem;
          align-items: center;
        }
        .filters-scroll {
          display: flex;
          gap: 0.4rem;
          flex-wrap: wrap;
          flex: 1;
          align-items: center;
        }
        .filter-icon {
          color: #5A5A5A;
          margin-right: 0.25rem;
          font-size: 0.75rem;
        }
        .filter-btn {
          padding: 0.35rem 0.875rem;
          border-radius: 20px;
          border: 1px solid #ddd;
          background: transparent;
          color: #5A5A5A;
          cursor: pointer;
          font-size: 0.75rem;
          font-weight: 400;
          transition: all 0.3s ease;
          font-family: "Inter", sans-serif;
          white-space: nowrap;
        }
        .filter-btn:hover {
          border-color: #D9A321;
        }
        .filter-btn.active {
          border-color: #D9A321;
          background: #D9A321;
          color: #FFFFFF;
          font-weight: 600;
        }
        .search-wrapper {
          position: relative;
          min-width: 180px;
          flex-shrink: 0;
        }
        .search-wrapper .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          font-size: 0.75rem;
        }
        .search-input {
          width: 100%;
          padding: 0.4rem 0.75rem 0.4rem 2rem;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          font-size: 0.813rem;
          outline: none;
          transition: border-color 0.3s ease;
          font-family: "Inter", sans-serif;
          background: #ffffff;
          color: #1F1F1F;
        }
        .search-input:focus {
          border-color: #D9A321;
          box-shadow: 0 0 0 3px rgba(217, 163, 33, 0.15);
        }
        @media (max-width: 576px) {
          .orders-filters {
            flex-direction: column;
          }
          .filters-scroll {
            width: 100%;
            overflow-x: auto;
            flex-wrap: nowrap;
            padding-bottom: 0.25rem;
          }
          .search-wrapper {
            width: 100%;
            min-width: auto;
          }
        }

        /* ========== COUNT ========== */
        .orders-count {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          font-size: 0.875rem;
          color: #6B7280;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        .clear-filter {
          background: none;
          border: none;
          color: #D9A321;
          cursor: pointer;
          font-size: 0.75rem;
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-family: "Inter", sans-serif;
        }
        .clear-filter:hover {
          color: #B8860B;
        }

        /* ========== ORDERS LIST ========== */
        .orders-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .orders-no-results {
          text-align: center;
          padding: 2rem;
          color: #6B7280;
        }

        .order-card {
          background: #FFFFFF;
          border-radius: 12px;
          border: 1px solid #e5e5e5;
          transition: all 0.3s ease;
        }
        .order-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }
        .order-card.bulk {
          border-left: 4px solid #D9A321;
        }

        .order-card__link {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem 1.25rem;
          text-decoration: none;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .order-card__left {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
          flex: 1;
        }
        .order-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #FFF;
          flex-shrink: 0;
        }
        .order-icon.regular {
          background: #006B2E;
        }
        .order-icon.bulk {
          background: #D9A321;
        }

        .order-number {
          font-weight: 600;
          color: #1F1F1F;
          font-size: 0.938rem;
        }
        .bulk-badge {
          margin-left: 0.5rem;
          font-size: 0.55rem;
          background: #D9A321;
          color: #FFF;
          padding: 0.15rem 0.5rem;
          border-radius: 10px;
          font-weight: 600;
          vertical-align: middle;
        }
        .preorder-badge {
          margin-left: 0.5rem;
          font-size: 0.55rem;
          background: #f59e0b;
          color: #FFF;
          padding: 0.15rem 0.5rem;
          border-radius: 10px;
          font-weight: 600;
          vertical-align: middle;
        }
        .preorder-badge svg {
          margin-right: 0.15rem;
        }

        .order-meta {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
          font-size: 0.7rem;
          color: #6B7280;
          margin-top: 0.15rem;
        }
        .order-meta span {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
        }
        .order-meta svg {
          font-size: 0.55rem;
          color: #D9A321;
        }

        .order-card__right {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          flex-wrap: wrap;
        }
        .order-total {
          text-align: right;
        }
        .total-label {
          font-size: 0.65rem;
          color: #6B7280;
        }
        .total-amount {
          font-weight: 700;
          color: #D9A321;
          font-size: 1rem;
        }

        .order-status {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.2rem 0.75rem;
          border-radius: 20px;
          color: #FFF;
          font-size: 0.65rem;
          font-weight: 600;
          white-space: nowrap;
        }

        .order-view {
          color: #006B2E;
          font-size: 0.813rem;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .order-view:hover {
          color: #D9A321;
        }

        @media (max-width: 576px) {
          .order-card__link {
            padding: 0.75rem 1rem;
            flex-direction: column;
            align-items: stretch;
          }
          .order-card__right {
            justify-content: space-between;
            width: 100%;
          }
          .order-meta {
            gap: 0.5rem;
          }
          .order-meta span {
            font-size: 0.6rem;
          }
        }

        /* ========== SUPPORT ========== */
        .orders-support {
          margin-top: 2rem;
          padding: 1rem 1.25rem;
          background: #F8F9FA;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 1rem;
          border: 1px solid #e5e5e5;
        }
        .orders-support svg {
          color: #25d366;
          font-size: 1.5rem;
        }
        .orders-support div {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .orders-support div strong {
          font-size: 0.875rem;
          color: #1F1F1F;
        }
        .orders-support div span {
          font-size: 0.75rem;
          color: #5A5A5A;
        }
        .support-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.4rem 1.25rem;
          background: #25d366;
          color: #FFFFFF;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          font-size: 0.813rem;
          transition: all 0.3s ease;
        }
        .support-link:hover {
          background: #1da851;
          transform: translateY(-2px);
        }

        @media (max-width: 480px) {
          .orders-support {
            flex-direction: column;
            text-align: center;
          }
          .support-link {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default Orders;